import { CheckPicker, Loader, SelectPicker, Tooltip, Whisper } from "rsuite";
import ProductInfoIcon from "../../assets/info-circle-grey.svg";
import { sortCommodityFirst, getDefaultCommodity } from "../../utils/helpers/commodityOrder";
import PurchaseSummary from "./summary/purchaseSummary";
import PurchaseOverviewVendor from "./summary/purchaseOverview";
import {
  useReportFilters,
  useReportFiltersData,
} from "../../react-query/hooks/HReports";
import { useEffect, useMemo, useState } from "react";
import {
  monthFormat,
  purChaseDropDownValues,
  stockDropdownValues,
} from "./ReportsConstants";
import {
  useReportTrendFilters,
  useReportTrendData,
  getReportTrendDataForExcel,
  downloadJPExclusions,
} from "../../react-query/hooks/HReportTrends";
import {
  useTMVsGrowthFilters,
  useGrowthVsTmData,
  getReportGrowthVsTmData,
} from "../../react-query/hooks/HReportGrowthVsTm";

import useResponsiveWidth from "../../react-query/hooks/useResponsiveWidth";
import TrendReports from "./trend-reports";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import moment from "moment";
import { dateFormatterToMMDD, numberFormatter } from "../../utils/formatter";
import { appInsights } from "../../router/router";
import { downloadExcel } from "../../utils/helpers/excelHelpers";

import { useLocation } from "react-router-dom";
import { trackTabClick } from "../../utils/helpers/trackingEvent";
import GrowthVsTM from "./growthVsTm";
import { convertDateToMonthYear } from "../../utils/helpers/mapModulesToIcon";
import { useAppSelector } from "../../services/redux/hooks/hooks";

const Reports = () => {
  const userDetails = useAppSelector((state) => {
    return state.user;
  });

  const width = useResponsiveWidth();
  const location = useLocation();
  const hiddenParam = location.state?.hiddenParam;
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<Array<string>>(
    [],
  );
  const [selectedTrendDistrict, setSelectedTrendDistrict] = useState<
    Array<string>
  >([]);
  const [selectedCommodityTrends, setSelectedCommodityTrends] =
    useState<string>("");
  const [selectedType, setSelectedType] = useState<string>("Monthly");
  const [selectedL1, setSelectedL1] = useState<Array<string>>([]);
  const [selectedL2, setSelectedL2] = useState<Array<string>>([]);
  const [selectedL3, setSelectedL3] = useState<Array<string>>([]);
  const [selectedVendor, setSelectedVendor] = useState<Array<string>>([]);
  const [selectedDistrict, setSelectedDistrict] = useState<Array<string>>([]);
  const [selectedStock, setSelectedStock] = useState<Array<string>>([]);
  const [selectedMonths, setSelectedMonths] = useState<Array<string>>([]);
  const [navItem, setNavItem] = useState<string>(
    hiddenParam === undefined || hiddenParam === "" ? "summary" : hiddenParam,
  );
  const [reportDistrictTrendFilter, setReportDistrictTrendFilter] = useState<
    Array<string>
  >([]);

  const [commGrowthFilter, setCommGrowthFilter] = useState<string>("All");
  const [salesGrowthFilter, setSalesGrowthFilter] = useState<string>("");
  const [tpGrowthFilter, setTpGrowthFilter] = useState<string>("");
  const [drill, setDrill] = useState<"Up" | "Down">("Down");
  const [activeIndex, setActiveIndex] = useState<string>("");
  const [jpToggle, setJpToggle] = useState<"JPIN" | "JPOUT">("JPIN");
  const isCopperWire = (value: string) =>
    value.trim().toLowerCase() === "copper wire";
  const showTrendExclusionFilter = isCopperWire(selectedCommodityTrends || "");

  const { GrowthVsTmFilterData, isLoading: isLoadingReportsGrowthVsTmFilters } =
    useTMVsGrowthFilters(navItem === "growthVsTM");

  const { reportFilterTrendData, isLoading: isLoadingReportTrendFilters } =
    useReportTrendFilters(localStorage.getItem("userEmail"), navItem);

  const { reportTrendData, isLoading: isLoadingReportTrends } =
    useReportTrendData(
      selectedCommodityTrends,
      selectedType,
      reportDistrictTrendFilter.length
        ? reportDistrictTrendFilter
        : reportFilterTrendData.district
          ? reportFilterTrendData.district.map((ele: any) => ele.district)
          : null,
      jpToggle === "JPIN" ? 1 : 0
    );

  const { reportGrowthVsTmData, isLoading: isLoadingReportGrowthVsTmData } =
    useGrowthVsTmData(
      commGrowthFilter,
      salesGrowthFilter,
      tpGrowthFilter,
      drill,
      activeIndex,
      jpToggle === "JPIN" ? 1 : 0
    );

  const reportsGVTMData = useMemo(() => {
    if (isLoadingReportGrowthVsTmData || !reportGrowthVsTmData) {
      return {
        graphData: [],
        axisData: { xSplit: 0, ySplit: 0 },
        tableData: [],
        XYTicksData: { xTicks: [0], yTicks: [0] },
      };
    }

    const axisData = reportGrowthVsTmData.axisData
      ? {
          xSplit: reportGrowthVsTmData.axisData.xSplit ?? 0,
          ySplit: reportGrowthVsTmData.axisData.ySplit ?? 0,
        }
      : { xSplit: 0, ySplit: 0 };

    const graphData = reportGrowthVsTmData.graphData
      ? reportGrowthVsTmData.graphData.filter((ele: any) =>
          drill === "Down"
            ? (userDetails.userCommArray.includes(ele.commodity) ?? false)
            : userDetails.userCommDisObj[commGrowthFilter]?.includes(
                ele.district,
              ),
        )
      : [];

    const tableData = reportGrowthVsTmData.tableData
      ? reportGrowthVsTmData.tableData.filter((ele: any) =>
          drill === "Down"
            ? (userDetails.userCommDisObj[commGrowthFilter]?.includes(
                ele.sub,
              ) ?? false)
            : true,
        )
      : [];

    let XYTicksData: { xTicks: number[]; yTicks: number[] } = {
      xTicks: [],
      yTicks: [],
    };

    const data = reportGrowthVsTmData?.graphData ?? [];
    const gapAbs = (a: number, b: number) => Math.abs(b - a);
    if (data.length) {
      // const xValues = data.map((ele: any) => ele.x);
      // if (xValues.length === 0) return { min: null, max: null };
      // const xMin = Math.min(...xValues);
      // const xMax = Math.max(...xValues);

      const xValues = data
        .map((p: any) => p.x)
        .filter((v: any) => Number.isFinite(v));

      const xMin = xValues.length ? Math.min(...xValues) : 0;
      const xMax = xValues.length ? Math.max(...xValues) : 0;

      const xLow = Math.floor(xMin);
      const xAbove = Math.ceil(xMax);

      const xInterval = Math.ceil((xMax - xMin) / data.length) || 1;

      let xArr: any = [];
      let xStart = xLow - 2;
      let xEnd = xAbove + 2;

      if (
        gapAbs(xStart, reportGrowthVsTmData?.axisData.xSplit) >
        gapAbs(reportGrowthVsTmData?.axisData.xSplit, xEnd)
      ) {
        xEnd =
          reportGrowthVsTmData?.axisData.xSplit +
          gapAbs(xStart, reportGrowthVsTmData?.axisData.xSplit);
      } else {
        xStart =
          reportGrowthVsTmData?.axisData.xSplit -
          gapAbs(reportGrowthVsTmData?.axisData.xSplit, xEnd);
      }

      const xRange = xEnd - xStart;
      const xTickCount = Math.max(data.length, 2);
      const xStep = xRange / (xTickCount - 1);
      for (let i = 0; i < xTickCount; i++) {
        xArr.push(xStart + xStep * i);
      }

      // const yValues = data.map((ele: any) => ele.y);

      // if (yValues.length === 0) return { min: null, max: null };
      // const yMin = Math.min(...yValues);
      // const yMax = Math.max(...yValues);

      const yValues = data
        .map((p: any) => p.y)
        .filter((v: any) => Number.isFinite(v));
      const yMin = yValues.length ? Math.min(...yValues) : 0;
      const yMax = yValues.length ? Math.max(...yValues) : 0;

      const yLow = Math.floor(yMin);
      const yAbove = Math.ceil(yMax);

      const yInterval = Math.ceil((yMax - yMin) / data.length) || 1;
      let yArr: any = [];
      let yStart = yLow - yInterval;
      let yEnd = yAbove + 2;

      if (
        gapAbs(yStart, reportGrowthVsTmData?.axisData.ySplit) >
        gapAbs(reportGrowthVsTmData?.axisData.ySplit, yEnd)
      ) {
        yEnd =
          reportGrowthVsTmData?.axisData.ySplit +
          gapAbs(yStart, reportGrowthVsTmData?.axisData.ySplit);
      } else {
        yStart =
          reportGrowthVsTmData?.axisData.ySplit -
          gapAbs(reportGrowthVsTmData?.axisData.ySplit, yEnd);
      }

      const yRange = yEnd - yStart;
      const yTickCount = Math.max(data.length, 2);
      const yStep = yRange / (yTickCount - 1);
      for (let i = 0; i < yTickCount; i++) {
        yArr.push(yStart + yStep * i);
      }
      XYTicksData = {
        xTicks: [...xArr.map((ele: any) => Number(ele.toFixed(2)))],
        yTicks: [...yArr.map((ele: any) => Number(ele.toFixed(2)))],
      };
    }

    return {
      graphData,
      axisData,
      tableData,
      XYTicksData,
    };
  }, [
    isLoadingReportGrowthVsTmData,
    reportGrowthVsTmData,
    drill,
    commGrowthFilter,
    userDetails,
  ]);

  const [filters, setFilters] = useState<any>({
    l1CategoryData: [],
    l2CategoryData: [],
    l3CategoryData: [],
    district: [],
    vendor: [],
    timePeriod: [],
    stock: [],
    sales: "Sales",
    month: [],
  });
  const { reportFilters, isLoading } = useReportFilters(
    localStorage.getItem("userEmail"),
    filters?.l1CategoryData,
    filters?.l2CategoryData,
    filters?.l3CategoryData,
    filters?.district,
    filters?.vendor,
    filters?.timePeriod,
    filters?.stock,
    filters?.sales,
    filters?.month,
  );
  const {
    treeData,
    totalSpend,
    totalVolume,
    purchaseOverviewData,
    isLoadingReportData,
    refreshDate,
    refetch,
  } = useReportFiltersData(
    filters?.l1CategoryData,
    filters?.l2CategoryData,
    filters?.l3CategoryData,
    filters?.district.length
      ? filters?.district
      : reportFilters.District && reportFilters.District.length
        ? reportFilters.District.map((ele: any) => ele.district)
        : null,
    filters?.vendor,
    filters?.timePeriod,
    filters?.stock,
    filters?.sales,
    filters?.month,
    jpToggle === "JPIN" ? 1 : 0
  );

  useEffect(() => {
    if (!isLoading) {
      refetch();
      let defaultYear = selectedTimePeriod;
      if (selectedTimePeriod?.length === 0) {
        defaultYear = reportFilters?.TimePeriod
          ? reportFilters?.TimePeriod[0]?.year
          : [];
        setSelectedTimePeriod(defaultYear);
      }

      setFilters({
        ...filters,
        timePeriod: [defaultYear],
      });
    }
  }, [isLoading]);

  useEffect(() => {
    if (!isLoadingReportTrends && selectedCommodityTrends === "") {
      if (reportFilterTrendData?.commodity) {
        setSelectedCommodityTrends(
          getDefaultCommodity(reportFilterTrendData?.commodity) ?? "",
        );
      }
    }
  }, [reportFilterTrendData]);

  useEffect(() => {
    if (navItem === "trendReports" && !showTrendExclusionFilter && jpToggle !== "JPIN") {
      setJpToggle("JPIN");
    }
  }, [
    jpToggle,
    navItem,
    showTrendExclusionFilter,
  ]);

  useEffect(() => {
    if (
      navItem === "growthVsTM" &&
      GrowthVsTmFilterData &&
      GrowthVsTmFilterData.sales &&
      GrowthVsTmFilterData.time_period &&
      salesGrowthFilter === "" &&
      tpGrowthFilter === ""
    ) {
      setSalesGrowthFilter(GrowthVsTmFilterData.sales[0].sales);
      setTpGrowthFilter(GrowthVsTmFilterData.time_period[0].baseline);
    }
  }, [navItem, GrowthVsTmFilterData]);

  const monthData = reportFilters?.Months?.map((item: any) => {
    return { month: monthFormat[item?.month] };
  });

  const handleExcelDownload = async (type: string) => {
    const excelData = await getReportTrendDataForExcel(
      selectedCommodityTrends,
      selectedType,
      selectedTrendDistrict.length !== 0
        ? selectedTrendDistrict
        : reportFilters.District && reportFilters.District.length
          ? reportFilters.District.map((ele: any) => ele.district)
          : null,
      jpToggle === "JPIN" ? 1 : 0,
    );

    const baseHeaders = [
      { header: "Commodity", key: "COMMODITY", width: 10 },
      { header: "District", key: "DISTRICT", width: 10 },
      {
        header: "Date",
        key: "MONTH_YEAR",
        width: 10,
        style: { numFmt: type === "Weekly" ? "yyyy-mm-dd" : "mmm-yyyy" },
      },
      {
        header: "Transaction Margin $",
        key: "TRANSACTION_MARGIN",
        width: 10,
        style: { numFmt: "$#,##0.00" },
      },
      { header: "Sales lbs", key: "SALES_VOLUME", width: 10 },
      {
        header: "Sales $",
        key: "SALES_DOLLAR",
        width: 10,
        style: { numFmt: "$#,##0.00" },
      },
      {
        header: "Transaction Margin %",
        key: "TRANSACTION_MARGIN_PERCENTAGE",
        width: 10,
        style: { numFmt: "0.00%" },
      },
    ];

    let formattedExcelData = excelData?.data?.data?.data?.map((item: any) => {
      let row: any = {};
      baseHeaders.forEach((header: { key: any }) => {
        const key: any = header.key;
        if (key === "MONTH_YEAR") {
          if (type === "Weekly")
            row[key] = new Date(item[key]).toLocaleDateString("en-us", {
              timeZone: "UTC",
            });
          else row[key] = new Date(item[key]);
        } else if (key === "TRANSACTION_MARGIN_PERCENTAGE") {
          row[key] = item[key] / 100;
        } else {
          row[key] = item[key];
        }
      });
      return row;
    });

    await downloadExcel(
      formattedExcelData,
      baseHeaders,
      `TM vs Sales_${selectedCommodityTrends}`,
      "TM vs Sales",
    );

    appInsights.trackEvent({
      name: "Reports: Trend Reports",
      properties: {
        description: `${localStorage.getItem(
          "userEmail",
        )} has downloaded "TM vs Sales_${selectedCommodityTrends}.xlsx" Report`,
      },
    });
  };

  const handleGrowthVsTmExcelDownload = async () => {
    const excelData = await getReportGrowthVsTmData(
      drill === "Down"
        ? [...GrowthVsTmFilterData.commodity.map((ele: any) => ele.commodity)]
        : commGrowthFilter,
      salesGrowthFilter,
      tpGrowthFilter,
      drill,
      drill === "Down"
        ? activeIndex
        : [...reportGrowthVsTmData.graphData.map((ele: any) => ele.district)],
    );
    let newExceldata = excelData?.data?.data;

    const newdata = {
      graphData: newExceldata.graphData
        ? [
            ...newExceldata.graphData.filter((ele: any) =>
              drill === "Down"
                ? (userDetails.userCommArray.includes(ele.commodity) ?? false)
                : userDetails.userCommDisObj[commGrowthFilter]?.includes(
                    ele.district,
                  ),
            ),
          ]
        : [],
      axisData: { ...newExceldata.axisData },
      tableData: newExceldata.tableData
        ? [
            ...newExceldata.tableData
              .filter((ele: any) =>
                drill === "Down"
                  ? (userDetails.userCommArray.includes(ele.dom) ?? false)
                  : userDetails.userCommDisObj[commGrowthFilter]?.includes(
                      ele.dom,
                    ),
              )
              .filter((ele: any) =>
                drill === "Down"
                  ? (userDetails.userCommDisObj[ele.dom]?.includes(ele.sub) ??
                    false)
                  : true,
              ),
          ]
        : [],
    };

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(
      `Growth vs TM_${drill === "Down" ? "All" : commGrowthFilter}`,
    );
    //adding filters
    worksheet.addRow(["Commodity:", commGrowthFilter]);
    worksheet.addRow(["Time Period:", tpGrowthFilter]);
    worksheet.addRow([
      "Unit",
      salesGrowthFilter === "Pounds(lbs)" ? "Pounds" : "Dollars",
    ]);

    worksheet.addRow([]); // Empty row

    //adding first heading
    worksheet.addRow([
      `${drill === "Down" ? "Commodity" : "District"} Level Data`,
    ]);

    //adding first table header
    worksheet.addRow([
      "",
      `YoY Sales ${salesGrowthFilter === "Pounds(lbs)" ? "(lbs)" : "($)"} Growth`,
      "TM%",
      `Sales ${salesGrowthFilter === "Pounds(lbs)" ? "(lbs)" : "($)"}`,
    ]);

    //adding first table data
    const table1Data = [
      ...newdata.graphData.map((ele: any) => [
        drill === "Down" ? ele.commodity : ele.district,
        ele.x / 100,
        ele.y / 100,
        ele.z,
      ]),
    ];
    worksheet.addRows(table1Data);

    worksheet.addRow([]); // Empty row

    //adding second heading
    worksheet.addRow([
      `${drill === "Down" ? "District" : "Sales Office"} Level Data`,
    ]);
    const secondRowStart =
      3 + //filter
      1 + //gap
      1 + //first heading
      1 + //first table header
      newdata.graphData.length + //first table data
      1 + //gap
      1;

    //adding second table header
    worksheet.addRow([
      `${drill === "Down" ? "Commodity" : "District"}`,
      `${drill === "Down" ? "District" : "Sales Office"}`,
      `YoY Sales ${salesGrowthFilter === "Pounds(lbs)" ? "(lbs)" : "($)"} Growth`,
      "TM%",
      `Sales ${salesGrowthFilter === "Pounds(lbs)" ? "(lbs)" : "($)"}`,
    ]);

    //adding second table data
    const table2Data = [
      ...newdata.tableData.map((ele: any) => [
        ele.dom,
        ele.sub,
        ele.growth / 100,
        ele.tm / 100,
        ele.sales,
      ]),
    ];
    worksheet.addRows(table2Data);

    // Apply styling to all data rows
    worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
      if (rowNumber <= 3) {
        row.getCell(1).font = { bold: true }; // Make the first cell bold
        row.eachCell({ includeEmpty: true }, (cell) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      } else if (
        rowNumber > 3 &&
        rowNumber <=
          3 + //filter
            1 + //gap
            1 + //first heading
            1 + //first table header
            newdata.graphData.length //first table data
      ) {
        row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
          if (rowNumber === 6 || rowNumber === 5) {
            row.font = { bold: true };
          }
          row.getCell(1).font = { bold: true }; // Make the first cell bold
          cell.alignment = {
            // wrapText: true,
            vertical: "middle",
            horizontal:
              rowNumber === 5 ? "center" : colNumber === 1 ? "left" : "right",
          };
          if ([4].includes(colNumber)) {
            cell.numFmt = "#,##0.00";
          }
          if ([2, 3].includes(colNumber)) {
            cell.numFmt = "0.0%";
          }
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      } else if (rowNumber >= secondRowStart) {
        row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
          if (
            rowNumber === secondRowStart ||
            rowNumber === secondRowStart + 1
          ) {
            row.font = { bold: true };
          }
          row.getCell(1).font = { bold: true }; // Make the first cell bold
          cell.alignment = {
            // wrapText: true,
            vertical: "middle",
            horizontal:
              rowNumber === secondRowStart + 1
                ? "center"
                : colNumber === 1 || colNumber === 2
                  ? "left"
                  : "right",
          };
          if ([5].includes(colNumber)) {
            cell.numFmt = "#,##0.00";
          }
          if ([3, 4].includes(colNumber)) {
            cell.numFmt = "0.0%";
          }
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      }
    });

    worksheet.columns.forEach((column: any) => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, (cell: any) => {
        if (cell.value) {
          const cellValue = cell.value.toString();
          maxLength = Math.max(maxLength, cellValue.length);
        }
      });
      column.width = maxLength + 2; // Add some padding to the width
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer]),
      `Growth vs TM_${drill === "Down" ? "All" : commGrowthFilter}.xlsx`,
    );

    appInsights.trackEvent({
      name: "Reports: Growth vs TM",
      properties: {
        description: `${localStorage.getItem(
          "userEmail",
        )} has downloaded "Growth vs TM_${drill === "Down" ? "All" : commGrowthFilter}.xlsx" Report`,
      },
    });
  };

  const handleJPExclusionsDownload = async () => {
    const response = await downloadJPExclusions(selectedCommodityTrends);

    const exclusionsByType = response?.data?.data?.data?.data || {};
    const workbook = new ExcelJS.Workbook();

    const sanitizeWorksheetName = (name: string) =>
      name.replace(/[\\/:*?\[\]]/g, "_").slice(0, 31) || "Sheet";

    Object.entries(exclusionsByType).forEach(([sheetName, rows]) => {
      if (!Array.isArray(rows)) {
        return;
      }

      const worksheet = workbook.addWorksheet(sanitizeWorksheetName(sheetName));

      const headerKeys = Array.from(
        rows.reduce<Set<string>>((acc, row) => {
          if (row && typeof row === "object" && !Array.isArray(row)) {
            Object.keys(row as Record<string, unknown>).forEach((key) =>
              acc.add(key),
            );
          }
          return acc;
        }, new Set<string>()),
      );

      if (!headerKeys.length) {
        worksheet.addRow(["No data"]);
        return;
      }

      worksheet.columns = headerKeys.map((key) => ({
        header: key,
        key,
        width: Math.max(20, key.length + 4),
      }));

      rows.forEach((row: any) => {
        const formattedRow: Record<string, string> = {};
        headerKeys.forEach((key) => {
          const value = row?.[key];
          formattedRow[key] = value === undefined || value === null ? "" : String(value);
        });
        worksheet.addRow(formattedRow);
      });

      const headerRow = worksheet.getRow(1);
      headerRow.eachCell((cell) => {
        cell.font = { bold: true };
        cell.alignment = { vertical: "middle", horizontal: "center" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
    });

    if (workbook.worksheets.length === 0) {
      const worksheet = workbook.addWorksheet("Exclusions");
      worksheet.addRow(["No exclusions data available"]);
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const fileName = `JP_Exclusions_${selectedCommodityTrends || "All"}.xlsx`;
    saveAs(new Blob([buffer]), fileName);

    appInsights.trackEvent({
      name: "Reports: Trend Reports",
      properties: {
        description: `${localStorage.getItem(
          "userEmail",
        )} has downloaded "${fileName}" Report`,
      },
    });
  };

  const handleTabonClick = (tabName: string, tabLabel: string) => {
    setNavItem(tabName);
    trackTabClick(tabLabel, "Reports");
  };

  return (
    <div className="reports-container flex flex-col justify-stretch items-stretch h-full volume-allocation">
      {(isLoading ||
        isLoadingReportData ||
        isLoadingReportTrends ||
        isLoadingReportTrendFilters) && (
        <Loader
          backdrop
          content="Loading..."
          size="md"
          vertical
          className="text-black font-bold"
          style={{ opacity: 0.8, zIndex: 9999 }}
        />
      )}
      <div className="grow shrink-0 basis-[17%] ps-[3%] pr-[5%] pt-[0.6%] flex flex-col flex-nowrap justify-start items-start gap-0 shadow-normal relative">
        <div className="flex justify-between items-center w-full">
          <div className="flex flex-col w-full">
            <div className="font-normal text-fs128">Reports</div>
            <div className="flex items-center">
              <span className="font-bold text-fs16 text-[#858585]">
                {navItem === "growthVsTM"
                  ? `Last refreshed month: `
                  : `Last refreshed date: `}
              </span>
              <span className="font-bold underline text-fs16 text-deloitteGreen pl-[0.25vh]">
                {navItem === "growthVsTM"
                  ? convertDateToMonthYear(refreshDate, -1)
                  : refreshDate}
              </span>
            </div>
          </div>
          {navItem === "summary" ? (
            <div className="flex justify-start items-center gap-1">
              <div className="flex flex-col justify-start items-start  max-w-[max(180px,10vw)]">
                <label className="font-bold text-fs16">L1 Category</label>
                <CheckPicker
                  data={reportFilters?.L1}
                  value={selectedL1}
                  labelKey="l1_category"
                  valueKey="l1_category"
                  style={{ width: width }}
                  onClean={() => {
                    setFilters({
                      ...filters,
                      l1CategoryData: [],
                    });
                  }}
                  onClose={() => {
                    setFilters({
                      ...filters,
                      l1CategoryData: selectedL1,
                    });
                  }}
                  onChange={(value: string[]) => {
                    if (value) {
                      setSelectedL1(value);
                    }
                  }}
                  placeholder="All"
                  cleanable={true}
                ></CheckPicker>
              </div>
              <div className="flex flex-col justify-start items-start  max-w-[max(180px,10vw)]">
                <label className="font-bold text-fs16">L2 Category</label>
                <CheckPicker
                  sticky
                  countable={true}
                  data={reportFilters?.L2}
                  preventOverflow={true}
                  cleanable={true}
                  style={{ width: width }}
                  onClean={() => {
                    setFilters({
                      ...filters,
                      l2CategoryData: [],
                    });
                  }}
                  onClose={() => {
                    setFilters({
                      ...filters,
                      l2CategoryData: selectedL2,
                    });
                  }}
                  onChange={(value: string[]) => {
                    if (value) {
                      setSelectedL2(value);
                    }
                  }}
                  value={selectedL2}
                  placeholder="All"
                  labelKey="l2_category"
                  valueKey="l2_category"
                  menuMaxHeight={150}
                />
              </div>
              <div className="flex flex-col justify-start items-start  max-w-[max(180px,10vw)]">
                <label className="font-bold text-fs16">L3 Category</label>
                <CheckPicker
                  sticky
                  countable={true}
                  data={reportFilters?.L3}
                  preventOverflow={true}
                  cleanable={true}
                  style={{ width: width }}
                  onClean={() => {
                    setFilters({
                      ...filters,
                      l3CategoryData: [],
                    });
                  }}
                  onClose={() => {
                    setFilters({
                      ...filters,
                      l3CategoryData: selectedL3,
                    });
                  }}
                  onChange={(value: string[]) => {
                    if (value) {
                      setSelectedL3(value);
                    }
                  }}
                  value={selectedL3}
                  placeholder="All"
                  labelKey="l3_category"
                  valueKey="l3_category"
                  menuMaxHeight={150}
                />
              </div>
              <div className="flex flex-col justify-start items-start  max-w-[max(180px,10vw)]">
                <label className="font-bold text-fs16">District</label>
                <CheckPicker
                  sticky
                  countable={true}
                  data={reportFilters?.District}
                  preventOverflow={true}
                  cleanable={true}
                  style={{ width: width }}
                  value={selectedDistrict}
                  onClean={() => {
                    setFilters({
                      ...filters,
                      district: [],
                    });
                  }}
                  onClose={() => {
                    setFilters({
                      ...filters,
                      district: selectedDistrict,
                    });
                  }}
                  onChange={(value: string[]) => {
                    if (value) {
                      setSelectedDistrict(value);
                    }
                  }}
                  placeholder="All"
                  labelKey="district"
                  valueKey="district"
                  menuMaxHeight={150}
                />
              </div>
              <div className="flex flex-col justify-start items-start  max-w-[max(180px,10vw)]">
                <label className="font-bold text-fs16">Supplier </label>
                <CheckPicker
                  placement="auto"
                  data={reportFilters?.Vendor}
                  countable={true}
                  style={{ width: width }}
                  value={selectedVendor}
                  onClean={() => {
                    setFilters({
                      ...filters,
                      vendor: [],
                    });
                  }}
                  onClose={() => {
                    setFilters({
                      ...filters,
                      vendor: selectedVendor,
                    });
                  }}
                  onChange={(value: string[]) => {
                    if (value) {
                      setSelectedVendor(value);
                    }
                  }}
                  labelKey="vendor"
                  valueKey="vendor"
                  placeholder="All"
                  cleanable={true}
                ></CheckPicker>
              </div>
              {/* <div className="invisible flex px-[2.5vh] custom-2000:p[5px] py-[1vh] custom-2000:px[10px] custom-2000:py-[10px]    text-fs12   text-white rounded-md bg-[#86BC25]">
                Reset
              </div> */}
            </div>
          ) : navItem === "trendReports" ? (
            <div className="flex justify-start items-center gap-1">
              <div className="flex flex-col justify-start items-start  max-w-[max(180px,10vw)]">
                <label className="font-bold text-fs16">Trend View</label>
                <SelectPicker
                  data={reportFilterTrendData?.type}
                  value={selectedType}
                  labelKey="type"
                  valueKey="type"
                  style={{ width: width }}
                  onChange={(value: any) => {
                    setSelectedType(value);
                  }}
                  placeholder="All"
                  cleanable={false}
                ></SelectPicker>
              </div>
              <div className="flex flex-col justify-start items-start  max-w-[max(180px,10vw)]">
                <label className="font-bold text-fs16">Commodity</label>
                <SelectPicker
                  data={sortCommodityFirst(reportFilterTrendData?.commodity)}
                  value={selectedCommodityTrends}
                  labelKey="commodity"
                  valueKey="commodity"
                  style={{ width: width }}
                  onChange={(value: any) => {
                    setSelectedCommodityTrends(value);
                  }}
                  placeholder="All"
                  cleanable={false}
                ></SelectPicker>
              </div>
              <div className="flex flex-col justify-start items-start  max-w-[max(180px,10vw)]">
                <label className="font-bold text-fs16">District</label>
                <CheckPicker
                  data={reportFilterTrendData?.district}
                  value={selectedTrendDistrict}
                  labelKey="district"
                  valueKey="district"
                  style={{ width: width }}
                  onClose={() => {
                    setReportDistrictTrendFilter(selectedTrendDistrict);
                  }}
                  onClean={() => {
                    setReportDistrictTrendFilter([]);
                  }}
                  onChange={(value: string[]) => {
                    if (value) setSelectedTrendDistrict(value);
                  }}
                  placeholder="All"
                  cleanable={true}
                ></CheckPicker>
              </div>
              {showTrendExclusionFilter && (
                <div className="flex flex-col justify-start items-start  max-w-[max(180px,10vw)]">
                  <label className="font-bold text-fs16">
                    Proj. through Stock
                  </label>
                  <SelectPicker
                    data={[
                      { label: "Exclude", value: "JPIN" },
                      { label: "Include", value: "JPOUT" },
                    ]}
                    value={jpToggle}
                    onChange={(value: string | null) => {
                      if (value) setJpToggle(value as "JPIN" | "JPOUT");
                    }}
                    style={{ width: width }}
                    cleanable={false}
                    searchable={false}
                  />
                </div>
              )}
            </div>
          ) : navItem === "growthVsTM" ? (
            <div className="flex justify-start items-center gap-1">
              {/* <div className="flex flex-col justify-start items-start  max-w-[max(180px,10vw)]">
                <label className="font-bold text-fs16">Commodity</label>
                <SelectPicker
                  data={
                    GrowthVsTmFilterData && GrowthVsTmFilterData.commodity
                      ? [
                          { commodity: "All" },
                          ...GrowthVsTmFilterData.commodity,
                        ]
                      : []
                  }
                  value={commGrowthFilter}
                  labelKey="commodity"
                  valueKey="commodity"
                  style={{ width: width }}
                  onChange={(value: any) => {
                    setCommGrowthFilter(value);
                    if (drill === "Down") {
                      setActiveIndex(value === "All" ? "" : value);
                    }
                  }}
                  placeholder="All"
                  cleanable={false}
                ></SelectPicker>
              </div>
              <div className="flex flex-col justify-start items-start  max-w-[max(180px,10vw)]">
                <label className="font-bold text-fs16">Sales Volume</label>
                <SelectPicker
                  data={
                    GrowthVsTmFilterData && GrowthVsTmFilterData.sales
                      ? GrowthVsTmFilterData.sales
                      : []
                  }
                  value={salesGrowthFilter}
                  labelKey="sales"
                  valueKey="sales"
                  style={{ width: width }}
                  onChange={(value: any) => {
                    setSalesGrowthFilter(value);
                  }}
                  placeholder="All"
                  cleanable={false}
                ></SelectPicker>
              </div> */}
              <div className="flex flex-col justify-start items-start  max-w-[max(180px,10vw)]">
                <label className="font-bold text-fs16">Time Period</label>
                <SelectPicker
                  data={
                    GrowthVsTmFilterData && GrowthVsTmFilterData.time_period
                      ? [
                          ...GrowthVsTmFilterData.time_period.map(
                            (ele: any) => {
                              if (ele.baseline !== "YTD") {
                                return { baseline: `Trailing ${ele.baseline}` };
                              } else return ele;
                            },
                          ),
                        ]
                      : []
                  }
                  value={tpGrowthFilter}
                  labelKey="baseline"
                  valueKey="baseline"
                  style={{ width: "10vw" }}
                  onChange={(value: any) => {
                    setTpGrowthFilter(value);
                  }}
                  // placeholder="All"
                  cleanable={false}
                ></SelectPicker>
              </div>
            </div>
          ) : null}
        </div>
        <div
          className={`flex justify-end mt-[10px] mb-[10px] items-center w-full ${
            navItem === "summary" ? "" : "hidden"
          }`}
        >
          <div className="flex justify-end items-center gap-1">
            {" "}
            <div className="flex flex-col justify-start items-start  max-w-[max(180px,10vw)]">
              <label className="font-bold text-fs16">Year</label>
              <SelectPicker
                data={reportFilters?.TimePeriod}
                labelKey="year"
                value={
                  Array.isArray(selectedTimePeriod)
                    ? selectedTimePeriod[0]
                    : selectedTimePeriod
                }
                valueKey="year"
                onChange={(value: any) => {
                  if (value) {
                    setSelectedTimePeriod(value);
                    setFilters({
                      ...filters,
                      timePeriod: [value],
                    });
                  }
                }}
                menuStyle={{ width: "100px" }}
                style={{ width: width }}
                menuMaxHeight={150}
                placeholder="Select Year"
                cleanable={false}
              ></SelectPicker>
            </div>
            <div className="flex flex-col justify-start items-start  max-w-[max(180px,10vw)]">
              <label className="font-bold text-fs16">Month</label>
              <CheckPicker
                data={monthData}
                labelKey="month"
                value={selectedMonths}
                valueKey="month"
                menuStyle={{ width: "100px" }}
                style={{ width: width }}
                menuMaxHeight={150}
                onClose={() => {
                  setFilters({
                    ...filters,
                    month: selectedMonths,
                  });
                }}
                onChange={(value: string[]) => {
                  if (value) {
                    setSelectedMonths(value);
                  }
                }}
                onClean={() => {
                  setFilters({
                    ...filters,
                    month: [],
                  });
                }}
                placeholder="All"
                cleanable={true}
              ></CheckPicker>
            </div>
            <div className="flex flex-col justify-start items-start  max-w-[max(180px,10vw)]">
              <label className="font-bold text-fs16">Stock vs. Project</label>
              <CheckPicker
                sticky
                data={stockDropdownValues}
                preventOverflow={true}
                cleanable={true}
                countable={false}
                value={selectedStock}
                style={{ width: width }}
                onClose={() => {
                  setFilters({
                    ...filters,
                    stock: selectedStock,
                  });
                }}
                onChange={(value: string[]) => {
                  if (value) {
                    setSelectedStock(value);
                  }
                }}
                placeholder="All"
                labelKey="stock"
                menuStyle={{ width: "100px" }}
                valueKey="stock"
                onClean={() => {
                  setFilters({
                    ...filters,
                    stock: [],
                  });
                }}
                menuMaxHeight={150}
              />
            </div>
            <div className="flex flex-col justify-start items-start  max-w-[max(180px,10vw)]">
              <label className="font-bold text-fs16">Purchase or Sales</label>
              <SelectPicker
                placement={"auto"}
                data={purChaseDropDownValues}
                preventOverflow={true}
                cleanable={false}
                style={{ width: width }}
                value={filters?.sales}
                onChange={(value: any) => {
                  setFilters({
                    ...filters,
                    sales: value,
                  });
                  if (value === "Purchase") {
                    setJpToggle("JPIN");
                  }
                }}
                placeholder="Select Purchase or Sales"
                labelKey="purchasesales"
                valueKey="purchasesales"
                menuStyle={{ width: "100px" }}
                menuMaxHeight={150}
              />
            </div>
            <div className="flex flex-col justify-start items-start  max-w-[max(180px,10vw)]">
              <label className="font-bold text-fs16">
                Proj. through Stock
              </label>
              <SelectPicker
                data={[
                  { label: "Exclude", value: "JPIN" },
                  { label: "Include", value: "JPOUT" },
                ]}
                value={jpToggle}
                onChange={(value: string | null) => {
                  if (value) setJpToggle(value as "JPIN" | "JPOUT");
                }}
                style={{ width: width }}
                cleanable={false}
                searchable={false}
                disabled={filters?.sales === "Purchase"}
              />
            </div>
            <div
              className="flex flex-col h-full relative top-[0.5rem]"
              onClick={() => {
                setSelectedDistrict([]);
                setSelectedL1([]);
                setSelectedL2([]);
                setSelectedL3([]);
                setSelectedMonths([]);
                setSelectedVendor([]);
                setSelectedTimePeriod(
                  reportFilters?.TimePeriod
                    ? reportFilters?.TimePeriod[0]?.year
                    : [],
                );
                setSelectedStock([]);
                setFilters({
                  l1CategoryData: [],
                  l2CategoryData: [],
                  l3CategoryData: [],
                  district: [],
                  vendor: [],
                  timePeriod: reportFilters?.TimePeriod
                    ? [reportFilters?.TimePeriod[0]?.year]
                    : [],
                  stock: [],
                  sales: "Sales",
                  month: [],
                });
              }}
            >
              <div className="cursor-pointer flex px-[2.5vh] custom-2000:relative custom-2000:top-[0.25rem] custom-2000:p[10px] py-[1vh] custom-2000:px[5px] custom-2000:py-[5px]    text-fs12   text-white rounded-md bg-[#86BC25]">
                Reset
              </div>
            </div>
          </div>
        </div>
        <div
          className={`flex absolute bottom-[1vh] ${navItem === "trendReports" || navItem === "growthVsTM" ? "w-[96%]" : "w-[50%]"} justify-between items-center gap-6 text-black font-normal text-[1.8vh]`}
        >
          <div>
            <span
              className={
                (navItem === "summary"
                  ? "font-bold border-b-2 border-black"
                  : "") + " cursor-pointer pb-[1vh] mr-[2vh]"
              }
              onClick={() => handleTabonClick("summary", "Reports Summary")}
            >
              Reports Summary
            </span>
            <span
              className={
                (navItem === "trendReports"
                  ? "font-bold border-b-2 border-black"
                  : "") + " cursor-pointer pb-[1vh] mr-[2vh]"
              }
              onClick={() => handleTabonClick("trendReports", "Trend Reports")}
            >
              Trend Reports
            </span>
            <span
              className={
                (navItem === "growthVsTM"
                  ? "font-bold border-b-2 border-black"
                  : "") + " cursor-pointer pb-[1vh] mr-[2vh]"
              }
              onClick={() => handleTabonClick("growthVsTM", "Growth vs TM")}
            >
              Growth vs TM
            </span>
          </div>
          {(navItem === "trendReports" || navItem === "growthVsTM") && (
            <div className="relative right-[4vw] flex items-center gap-[0.5vw]">
              {navItem === "trendReports" && showTrendExclusionFilter && (
                <div
                  onClick={() => {
                    handleJPExclusionsDownload();
                  }}
                  className="text-white bold text-[0.8vw] bg-[#86BC25] p-[0.4vw] rounded-md cursor-pointer flex items-center"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="1vw"
                    height="1vw"
                    viewBox="0 0 12 16"
                    fill="none"
                  >
                    <path
                      d="M14.6668 5.33464V9.20797C14.2602 8.97464 13.8135 8.81464 13.3335 8.72797V5.33464H2.66683V12.0013H8.72683C8.6935 12.2213 8.66683 12.4413 8.66683 12.668C8.66683 12.8946 8.6935 13.1146 8.72683 13.3346H2.66683C1.9335 13.3346 1.3335 12.7413 1.3335 12.0013V4.0013C1.3335 3.2613 1.92683 2.66797 2.66683 2.66797H6.66683L8.00016 4.0013H13.3335C14.0668 4.0013 14.6668 4.59464 14.6668 5.33464ZM13.3335 10.668H12.0002V13.3346H10.6668L12.6668 15.3346L14.6668 13.3346H13.3335V10.668Z"
                      fill="white"
                    />
                  </svg>
                  <span className="ml-[3px]">Exclusions</span>
                </div>
              )}
              <div
                onClick={() => {
                  if (navItem === "trendReports")
                    handleExcelDownload(selectedType);
                  if (navItem === "growthVsTM") {
                    handleGrowthVsTmExcelDownload();
                  }
                }}
                className="text-white bold text-[0.8vw] bg-[#86BC25] p-[0.4vw] rounded-md cursor-pointer flex items-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="1vw"
                  height="1vw"
                  viewBox="0 0 12 16"
                  fill="none"
                >
                  <path
                    d="M14.6668 5.33464V9.20797C14.2602 8.97464 13.8135 8.81464 13.3335 8.72797V5.33464H2.66683V12.0013H8.72683C8.6935 12.2213 8.66683 12.4413 8.66683 12.668C8.66683 12.8946 8.6935 13.1146 8.72683 13.3346H2.66683C1.9335 13.3346 1.3335 12.7413 1.3335 12.0013V4.0013C1.3335 3.2613 1.92683 2.66797 2.66683 2.66797H6.66683L8.00016 4.0013H13.3335C14.0668 4.0013 14.6668 4.59464 14.6668 5.33464ZM13.3335 10.668H12.0002V13.3346H10.6668L12.6668 15.3346L14.6668 13.3346H13.3335V10.668Z"
                    fill="white"
                  />
                </svg>
                <span className="ml-[3px]">Download</span>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="bg-[#F1F1F1] grow shrink p-[1.5%] below-1200:p-[1%] pr-[2%] flex flex-col flex-nowrap justify-start items-start gap-6 h-full w-full">
        {navItem === "summary" && (
          <div className="flex h-full w-full gap-4">
            <div className="shadow-module-card h-[full] w-[50%] custom-1600:h-[92%] below-1100:h-[85%] bg-white">
              <PurchaseSummary
                purchaseOrSales={filters?.sales}
                treeData={treeData}
                totalSpend={totalSpend}
                totalVolume={totalVolume}
              />
            </div>
            <div className="shadow-module-card h-[full] w-[50%] custom-1600:h-[92%] below-1100:h-[85%]  bg-white">
              <PurchaseOverviewVendor
                purchaseOrSales={filters?.sales}
                purchaseOverviewData={purchaseOverviewData}
              />
            </div>
          </div>
        )}
        {navItem === "trendReports" &&
          (isLoadingReportTrends ? null : (
            <TrendReports
              selectedType={selectedType}
              reportTrendData={reportTrendData}
              refreshDate={refreshDate}
            />
          ))}
        {navItem === "growthVsTM" && (
          <GrowthVsTM
            commGrowthFilter={commGrowthFilter}
            setCommGrowthFilter={setCommGrowthFilter}
            salesGrowthFilter={salesGrowthFilter}
            setSalesGrowthFilter={setSalesGrowthFilter}
            tpGrowthFilter={tpGrowthFilter}
            setTpGrowthFilter={setTpGrowthFilter}
            drill={drill}
            setDrill={setDrill}
            activeIndex={activeIndex}
            setActiveIndex={setActiveIndex}
            salesData={
              GrowthVsTmFilterData && GrowthVsTmFilterData.sales
                ? GrowthVsTmFilterData.sales
                : []
            }
            data={{
              graphData: reportsGVTMData.graphData,
              axisData: reportsGVTMData.axisData,
              tableData: reportsGVTMData.tableData,
              XYTicksData: reportsGVTMData.XYTicksData,
            }}
            isLoadingReportGrowthVsTmData={isLoadingReportGrowthVsTmData}
          ></GrowthVsTM>
        )}
      </div>
    </div>
  );
};

export default Reports;
